import React, { useState, useCallback, useRef, useEffect } from 'react';
import { TOOL_TYPES, ALL_FIBONACCI_LEVELS } from '../utils/drawingToolsManager';
import { Button } from './ui/button';

/**
 * DrawingToolbar – a dropdown menu button for chart drawing tools.
 * Sits next to the Indicators button in the top toolbar.
 *
 * Props:
 *   activeTool         : current TOOL_TYPES value
 *   onSelectTool       : (toolType) => void
 *   onDeleteSelected   : () => void
 *   onDeleteAll        : () => void
 *   onUpdateDrawing    : (id, { color, lineWidth }) => void
 *   onSelectDrawing    : (id) => void
 *   hasSelection       : boolean
 *   selectedDrawingId  : string|null
 *   drawingCount       : number
 *   drawings           : array of drawing objects
 *   compact            : boolean
 */

const DRAWING_COLORS = [
  '#FFD700', '#00BFFF', '#EF4444', '#22C55E', '#A855F7',
  '#F97316', '#EC4899', '#14B8A6', '#FFFFFF', '#94A3B8',
];

const LINE_WIDTHS = [1, 1.5, 2, 3, 4];

const TOOL_LABELS = {
  [TOOL_TYPES.HORIZONTAL_LINE]: 'Horizontal Line',
  [TOOL_TYPES.TREND_LINE]: 'Trend Line',
  [TOOL_TYPES.FIBONACCI]: 'Fibonacci Retracement',
  [TOOL_TYPES.CHANNEL]: 'Channel',
};

const CHART_TOOLBAR_BUTTON_STYLE = {
  textTransform: 'none',
  letterSpacing: '0.01em',
  fontWeight: 600,
};

const getChartToolbarLauncherClassName = (active, compact = false) => [
  'chart-toolbar-button',
  active ? 'chart-toolbar-launcher-active' : '',
  compact ? 'chart-toolbar-button-compact' : '',
].filter(Boolean).join(' ');

const DrawingToolbar = ({
  activeTool,
  onSelectTool,
  onDeleteSelected,
  onDeleteAll,
  onUpdateDrawing,
  onSelectDrawing,
  hasSelection = false,
  selectedDrawingId = null,
  drawingCount = 0,
  drawings = [],
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const menuRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
        setEditingId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = useCallback((tool) => {
    onSelectTool(activeTool === tool ? TOOL_TYPES.NONE : tool);
  }, [activeTool, onSelectTool]);

  const handleEditToggle = useCallback((id) => {
    setEditingId(prev => prev === id ? null : id);
    onSelectDrawing?.(id);
  }, [onSelectDrawing]);

  const handleColorChange = useCallback((id, color) => {
    onUpdateDrawing?.(id, { color });
  }, [onUpdateDrawing]);

  const handleLineWidthChange = useCallback((id, lineWidth) => {
    onUpdateDrawing?.(id, { lineWidth });
  }, [onUpdateDrawing]);

  const handleToggleFibLevel = useCallback((id, level, currentLevels) => {
    const levels = currentLevels || [...ALL_FIBONACCI_LEVELS];
    const newLevels = levels.includes(level)
      ? levels.filter(l => l !== level)
      : [...levels, level].sort((a, b) => a - b);
    // Don't allow removing all levels
    if (newLevels.length === 0) return;
    onUpdateDrawing?.(id, { fibLevels: newLevels });
  }, [onUpdateDrawing]);

  const handleToggleStretched = useCallback((id, currentStretched) => {
    onUpdateDrawing?.(id, { stretched: !currentStretched });
  }, [onUpdateDrawing]);

  const handleDeleteDrawing = useCallback((id) => {
    onSelectDrawing?.(id);
    // Small delay so selection registers before delete
    setTimeout(() => {
      onDeleteSelected?.();
      setEditingId(null);
    }, 10);
  }, [onSelectDrawing, onDeleteSelected]);

  const tools = [
    { type: TOOL_TYPES.HORIZONTAL_LINE, icon: <HorizontalLineIcon /> },
    { type: TOOL_TYPES.TREND_LINE, icon: <TrendLineIcon /> },
    { type: TOOL_TYPES.FIBONACCI, icon: <FibonacciIcon /> },
    { type: TOOL_TYPES.CHANNEL, icon: <ChannelIcon /> },
  ];

  const isActive = activeTool !== TOOL_TYPES.NONE;

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <Button
        size={compact ? 'sm' : 'md'}
        variant="ghost"
        onClick={() => setOpen(v => !v)}
        className={getChartToolbarLauncherClassName(isActive || drawingCount > 0, compact)}
        style={CHART_TOOLBAR_BUTTON_STYLE}
      >
        <span className="flex items-center gap-1.5">
          <DrawIcon />
          <span>Draw</span>
          {drawingCount > 0 && (
            <span className="chart-toolbar-badge">
              {drawingCount}
            </span>
          )}
        </span>
      </Button>

      {open && (
        <div className="absolute left-0 mt-2 w-72 rounded-lg bg-slate-800 border border-slate-700 shadow-xl z-50 overflow-hidden">
          {/* Tool selection row */}
          <div className="p-2 border-b border-slate-700">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 px-1">Drawing Tools</div>
            <div className="flex items-center gap-1">
              {tools.map(({ type, icon }) => (
                <button
                  key={type}
                  onClick={() => { toggle(type); setOpen(false); }}
                  title={TOOL_LABELS[type]}
                  className={`
                    flex-1 h-8 flex items-center justify-center rounded-md transition-colors
                    ${activeTool === type
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700'
                    }
                  `}
                >
                  {icon}
                </button>
              ))}
            </div>
            {activeTool !== TOOL_TYPES.NONE && (
              <div className="mt-1.5 flex items-center gap-2 px-1">
                <span className="text-[11px] text-blue-400">
                  Active: {TOOL_LABELS[activeTool]}
                </span>
                <button
                  onClick={() => { onSelectTool(TOOL_TYPES.NONE); }}
                  className="text-[10px] text-slate-500 hover:text-slate-300 underline"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Drawings list */}
          <div className="max-h-64 overflow-y-auto">
            {drawings.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-500 text-center">
                No drawings yet. Select a tool above to start drawing.
              </div>
            ) : (
              drawings.map((d) => {
                const isEditing = editingId === d.id;
                const isSelOnChart = selectedDrawingId === d.id;
                const label = TOOL_LABELS[d.type] || d.type;
                const color = d.color || '#FFD700';
                const lw = d.lineWidth || 1.5;
                const isFib = d.type === TOOL_TYPES.FIBONACCI;
                const fibLevels = d.fibLevels || ALL_FIBONACCI_LEVELS;
                const isStretched = d.stretched === true;

                return (
                  <div
                    key={d.id}
                    className={`border-b border-slate-700/50 last:border-b-0 ${isSelOnChart ? 'bg-slate-700/40' : ''}`}
                  >
                    {/* Drawing row */}
                    <div className="flex items-center gap-2 px-3 py-1.5">
                      <span
                        className="w-3 h-3 rounded-sm flex-shrink-0 border border-slate-600"
                        style={{ backgroundColor: color }}
                      />
                      <button
                        className="flex-1 text-left text-[12px] text-slate-300 hover:text-white truncate"
                        onClick={() => onSelectDrawing?.(d.id)}
                        title="Select on chart"
                      >
                        {label}
                      </button>
                      <button
                        onClick={() => handleEditToggle(d.id)}
                        title="Edit style"
                        className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${
                          isEditing ? 'text-blue-400 bg-slate-700' : 'text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        <PencilIcon />
                      </button>
                      <button
                        onClick={() => handleDeleteDrawing(d.id)}
                        title="Delete"
                        className="w-6 h-6 flex items-center justify-center rounded text-slate-500 hover:text-red-400 transition-colors"
                      >
                        <TrashIcon />
                      </button>
                    </div>

                    {/* Inline editor */}
                    {isEditing && (
                      <div className="px-3 pb-2 space-y-2">
                        {/* Color picker */}
                        <div>
                          <div className="text-[10px] text-slate-500 mb-1">Color</div>
                          <div className="flex flex-wrap gap-1.5">
                            {DRAWING_COLORS.map(c => (
                              <button
                                key={c}
                                onClick={() => handleColorChange(d.id, c)}
                                className={`w-5 h-5 rounded-sm border transition-transform ${
                                  color === c ? 'border-white scale-110' : 'border-slate-600 hover:border-slate-400'
                                }`}
                                style={{ backgroundColor: c }}
                                title={c}
                              />
                            ))}
                            <label className="w-5 h-5 rounded-sm border border-slate-600 hover:border-slate-400 overflow-hidden cursor-pointer flex items-center justify-center" title="Custom color">
                              <input
                                type="color"
                                value={color}
                                onChange={(e) => handleColorChange(d.id, e.target.value)}
                                className="opacity-0 absolute w-0 h-0"
                              />
                              <span className="text-[9px] text-slate-400">+</span>
                            </label>
                          </div>
                        </div>
                        {/* Line width */}
                        <div>
                          <div className="text-[10px] text-slate-500 mb-1">Line Width</div>
                          <div className="flex items-center gap-1.5">
                            {LINE_WIDTHS.map(w => (
                              <button
                                key={w}
                                onClick={() => handleLineWidthChange(d.id, w)}
                                className={`h-7 px-2 rounded text-[11px] transition-colors ${
                                  lw === w
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-slate-700 text-slate-400 hover:text-white hover:bg-slate-600'
                                }`}
                              >
                                {w}px
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Fibonacci-specific options */}
                        {isFib && (
                          <>
                            {/* Stretch toggle */}
                            <div>
                              <div className="text-[10px] text-slate-500 mb-1">Display</div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleToggleStretched(d.id, isStretched)}
                                  className={`h-7 px-2.5 rounded text-[11px] transition-colors ${
                                    !isStretched
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-slate-700 text-slate-400 hover:text-white hover:bg-slate-600'
                                  }`}
                                >
                                  Bounded
                                </button>
                                <button
                                  onClick={() => handleToggleStretched(d.id, isStretched)}
                                  className={`h-7 px-2.5 rounded text-[11px] transition-colors ${
                                    isStretched
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-slate-700 text-slate-400 hover:text-white hover:bg-slate-600'
                                  }`}
                                >
                                  Stretched
                                </button>
                              </div>
                            </div>
                            {/* Fib levels toggle */}
                            <div>
                              <div className="text-[10px] text-slate-500 mb-1">Levels</div>
                              <div className="flex flex-wrap gap-1">
                                {ALL_FIBONACCI_LEVELS.map(level => {
                                  const isOn = fibLevels.includes(level);
                                  const pct = level <= 1
                                    ? `${(level * 100).toFixed(level % 0.1 === 0 ? 0 : 1)}%`
                                    : `${(level * 100).toFixed(1)}%`;
                                  return (
                                    <button
                                      key={level}
                                      onClick={() => handleToggleFibLevel(d.id, level, fibLevels)}
                                      className={`h-6 px-1.5 rounded text-[10px] transition-colors ${
                                        isOn
                                          ? 'bg-blue-600/80 text-white'
                                          : 'bg-slate-700 text-slate-500 hover:text-slate-300 hover:bg-slate-600'
                                      }`}
                                    >
                                      {pct}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer actions */}
          {drawings.length > 0 && (
            <div className="p-2 border-t border-slate-700 flex justify-end">
              <button
                onClick={() => { onDeleteAll?.(); setEditingId(null); }}
                className="text-[11px] text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-slate-700 transition-colors"
              >
                Delete All ({drawings.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DrawingToolbar;

// ─── SVG Icons ────────────────────────────────────────────────────────────────

const DrawIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 17l4-1L16.5 6.5a1.4 1.4 0 0 0-2-2L5 14l-1 4z" />
    <path d="M14.5 4.5l2 2" />
  </svg>
);

const HorizontalLineIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <line x1="2" y1="10" x2="18" y2="10" />
    <circle cx="4" cy="10" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="16" cy="10" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

const TrendLineIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <line x1="3" y1="15" x2="17" y2="5" />
    <circle cx="3" cy="15" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="17" cy="5" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

const FibonacciIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.2">
    <line x1="2" y1="3" x2="18" y2="3" />
    <line x1="2" y1="7" x2="18" y2="7" strokeDasharray="2 2" />
    <line x1="2" y1="10" x2="18" y2="10" strokeDasharray="2 2" />
    <line x1="2" y1="13" x2="18" y2="13" strokeDasharray="2 2" />
    <line x1="2" y1="17" x2="18" y2="17" />
  </svg>
);

const ChannelIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <line x1="2" y1="14" x2="18" y2="8" />
    <line x1="2" y1="8" x2="18" y2="2" strokeDasharray="3 2" />
  </svg>
);

const PencilIcon = () => (
  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2 14l1.5-4L12 1.5a1.1 1.1 0 0 1 1.5 1.5L5 11.5 2 14z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5">
    <polyline points="3 5 4 5 13 5" />
    <path d="M12 5l-.6 8a1.2 1.2 0 0 1-1.2 1.1H5.8A1.2 1.2 0 0 1 4.6 13L4 5m2 0V3.5a.8.8 0 0 1 .8-.8h2.4a.8.8 0 0 1 .8.8V5" />
  </svg>
);
